import React, { useEffect, useState, useMemo } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ROLE_LABELS } from '@/lib/auth';
import { ADMIN } from '@/constants/testIds';
import {
  Users, Zap, KeyRound, Phone, Building2, ShieldCheck, Info,
  RefreshCw, MessageCircle, Search, GitBranch,
} from 'lucide-react';
import WhatsAppSendDialog from '@/components/whatsapp/WhatsAppSendDialog';

const EMPTY = {
  email: '', full_name: '', role: 'audiologist', password: '',
  phone: '', clinic_ids: [], active: true, reports_to_user_id: '',
};

// Role hierarchy scope description for the "Roles & Permissions" panel
const ROLE_INFO = [
  { key: 'super_admin', scope: 'Full org · manage users, config, integrations, delete records' },
  { key: 'ceo', scope: 'Full org · read + user management + all reports · cannot alter integrations' },
  { key: 'central_team', scope: 'Cross-region operations · scheduling · reports · patient orchestration' },
  { key: 'business_head', scope: 'Multi-region · reports, patients, teams · no config' },
  { key: 'business_manager', scope: 'One region · P&L · staff performance · reports' },
  { key: 'regional_head', scope: 'One region · reports & operations across clusters' },
  { key: 'cluster_manager', scope: 'One cluster · staff scheduling · reports · reassignments' },
  { key: 'cluster_audio_head', scope: 'One cluster · audiology quality · trials · training' },
  { key: 'clinic_manager', scope: 'One clinic · full patient CRUD · WhatsApp · staff · reset passwords' },
  { key: 'clinic_head', scope: 'One clinic · daily ops · own team · reports for their clinic' },
  { key: 'clinic_pc', scope: 'One clinic · patient coordinator · check-in · reschedule · notes' },
  { key: 'central_scheduler', scope: 'Cross-clinic scheduling only — no patient edits' },
  { key: 'front_desk', scope: 'One clinic · check-in, confirm, reschedule · notes' },
  { key: 'ent_doctor', scope: 'View patients · add clinical notes · own calendar' },
  { key: 'audiologist', scope: 'Own patients & calendar · dispositions · WhatsApp send' },
];

export default function UsersTab() {
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [rolesPanelOpen, setRolesPanelOpen] = useState(false);
  const [hierarchyOpen, setHierarchyOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(null);   // {id, email} or null
  const [waOpen, setWaOpen] = useState(null);          // {phone, name} or null
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const [u, c] = await Promise.all([api.get('/users'), api.get('/clinics')]);
      setRows(u.data); setClinics(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return rows.filter(r => {
      if (roleFilter !== 'all' && r.role !== roleFilter) return false;
      const q = query.toLowerCase().trim();
      if (!q) return true;
      return (r.full_name || '').toLowerCase().includes(q)
        || (r.email || '').toLowerCase().includes(q)
        || (r.phone || '').includes(q);
    });
  }, [rows, query, roleFilter]);

  const clinicById = useMemo(() => Object.fromEntries(clinics.map(c => [c.id, c])), [clinics]);
  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, password: '' }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        if (!form.password || form.password.length < 6) { toast.error('Password must be ≥ 6 chars'); return; }
        await api.post('/users', form);
      }
      toast.success('Saved'); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.email}?`)) return;
    try { await api.delete(`/users/${r.id}`); toast.success('Deleted'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const doReset = async () => {
    if (!resetOpen) return;
    try {
      const r = await api.post(`/users/${resetOpen.id}/reset-password`, { new_password: 'HearClear@2026' });
      toast.success(`Password reset. Temp: ${r.data.new_password}`);
      setResetOpen(null); load();
    } catch { toast.error('Failed to reset'); }
  };

  const toggleClinic = (cid) => setForm(f => {
    const has = (f.clinic_ids || []).includes(cid);
    return { ...f, clinic_ids: has ? f.clinic_ids.filter(x => x !== cid) : [...(f.clinic_ids || []), cid] };
  });

  return (
    <div>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <div>
          <h2 className="font-heading text-xl font-bold text-stone-900">Users & Roles</h2>
          <p className="text-xs text-stone-500 mt-0.5">
            Login accounts, clinic access & role assignments. {rows.length} active users · Fundle bridge available.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setRolesPanelOpen(true)} data-testid="users-roles-panel-btn">
            <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Roles & Permissions
          </Button>
          <Button variant="outline" size="sm" onClick={() => setHierarchyOpen(true)} data-testid="users-hierarchy-btn">
            <GitBranch className="w-3.5 h-3.5 mr-1.5" /> View Hierarchy
          </Button>
          <Button variant="outline" size="sm" onClick={() => setProvisionOpen(true)}
            className="border-emerald-300 text-emerald-800 hover:bg-emerald-50" data-testid="users-provision-btn">
            <Zap className="w-3.5 h-3.5 mr-1.5" /> Provision from Fundle
          </Button>
          <Button size="sm" className="bg-stone-900 hover:bg-stone-800 text-white h-8"
            onClick={openCreate} data-testid={ADMIN.createButton('user')}>
            <Users className="w-3.5 h-3.5 mr-1.5" /> New User
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2 top-2.5" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, phone…"
            className="h-8 w-72 pl-7 text-xs" data-testid="users-search" />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            {Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <span className="text-[10px] text-stone-500">{filtered.length} of {rows.length}</span>
      </div>

      <AdminTable
        loading={loading} rows={filtered}
        columns={[
          { key: 'full_name', label: 'Name', render: (r) => (
            <div>
              <div className="font-medium text-stone-900">{r.full_name}</div>
              {r.must_change_password && (
                <Badge className="mt-0.5 bg-amber-100 text-amber-800 text-[9px]" data-testid="badge-temp-pw">
                  <KeyRound className="w-2.5 h-2.5 mr-0.5" /> Temp password
                </Badge>
              )}
              {r.fundle_user_id != null && (
                <span className="ml-1 text-[9px] text-stone-400 font-mono">fundle:{r.fundle_user_id}</span>
              )}
            </div>
          )},
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Mobile', render: (r) => r.phone
            ? <span className="font-mono text-xs">{r.phone}</span> : '—' },
          { key: 'role', label: 'Role', render: (r) => (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-stone-100 rounded font-semibold">
              {ROLE_LABELS[r.role] || r.role}
            </span>
          )},
          { key: 'clinic_ids', label: 'Clinics', render: (r) => {
            const ids = r.clinic_ids || [];
            if (ids.length === 0) return <span className="text-stone-400">—</span>;
            return (
              <div className="flex flex-wrap gap-1">
                {ids.slice(0, 2).map(id => (
                  <span key={id} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-200">
                    {clinicById[id]?.name || id.slice(0, 6)}
                  </span>
                ))}
                {ids.length > 2 && <span className="text-[10px] text-stone-500">+{ids.length - 2}</span>}
              </div>
            );
          }},
          { key: 'active', label: 'Active', render: (r) => r.active
            ? <span className="text-emerald-600">✓</span>
            : <span className="text-red-500">✕</span> },
          { key: 'reports_to_user_id', label: 'Reports to', render: (r) => {
            const m = rows.find(x => x.id === r.reports_to_user_id);
            return m
              ? <span className="text-xs text-stone-700">{m.full_name}</span>
              : <span className="text-stone-400">—</span>;
          }},
          { key: 'actions', label: '', render: (r) => (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => setResetOpen({ id: r.id, email: r.email })}
                title="Reset password" data-testid={`user-reset-${r.id}`}>
                <KeyRound className="w-3.5 h-3.5 text-amber-600" />
              </Button>
              {r.phone && (
                <Button variant="ghost" size="icon" className="h-7 w-7"
                  onClick={() => setWaOpen({ phone: r.phone, name: r.full_name })}
                  title="Send WhatsApp" data-testid={`user-wa-${r.id}`}>
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-600" />
                </Button>
              )}
            </div>
          )},
        ]}
        rowTestId={ADMIN.row}
        onEdit={openEdit} onDelete={remove}
      />

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit User' : 'New User'}</DialogTitle>
            <DialogDescription className="text-xs">
              {editing ? 'Update account, role or clinic access.' : 'Create a login. Password will be required for first sign-in.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="h-9" data-testid="user-field-name" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9" disabled={!!editing} data-testid="user-field-email" />
            </div>
            <div>
              <Label>Mobile</Label>
              <Input value={form.phone || ''} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9" placeholder="+91 …" />
            </div>
            <div>
              <Label>Password {editing && <span className="text-xs text-stone-400">(leave blank to keep)</span>}</Label>
              <Input type="password" value={form.password || ''} onChange={(e) => setForm({ ...form, password: e.target.value })} className="h-9" />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Clinic access
                <span className="text-[10px] text-stone-500 ml-1">({form.clinic_ids?.length || 0} selected)</span>
              </Label>
              <div className="mt-1 max-h-32 overflow-y-auto border border-stone-200 rounded p-1.5 grid grid-cols-2 gap-1">
                {clinics.map(c => {
                  const on = (form.clinic_ids || []).includes(c.id);
                  return (
                    <button key={c.id} type="button" onClick={() => toggleClinic(c.id)}
                      className={`text-left text-xs px-1.5 py-1 rounded border transition-colors ${on ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-white border-stone-200 hover:bg-stone-50'}`}>
                      <span className="mr-1">{on ? '✓' : '○'}</span> {c.name}
                    </button>
                  );
                })}
                {clinics.length === 0 && <span className="text-stone-400 text-xs p-1">No clinics — sync from Fundle first.</span>}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Active</Label>
            </div>
            <div>
              <Label className="flex items-center gap-1.5">
                <GitBranch className="w-3.5 h-3.5" /> Reports to
              </Label>
              <Select
                value={form.reports_to_user_id || 'none'}
                onValueChange={(v) => setForm({ ...form, reports_to_user_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="h-9 mt-1" data-testid="user-field-reports-to"><SelectValue placeholder="No manager" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">— No manager —</SelectItem>
                  {rows
                    .filter(r => !editing || r.id !== editing.id)
                    .map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.full_name} <span className="text-[10px] text-stone-400 ml-1">· {ROLE_LABELS[r.role] || r.role}</span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <div className="text-[10px] text-stone-500 mt-0.5">
                Sets the org tree: CEO → Central Team → Business Head → Business Manager → Cluster Audio Head → Clinic Head → Clinic PC.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password confirmation */}
      <Dialog open={!!resetOpen} onOpenChange={(v) => !v && setResetOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset password?</DialogTitle>
            <DialogDescription className="text-xs">
              This will set <code className="text-stone-800">HearClear@2026</code> as the temporary password for {resetOpen?.email} and force them to change it on next login.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(null)}>Cancel</Button>
            <Button onClick={doReset} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="user-reset-confirm">
              <KeyRound className="w-3.5 h-3.5 mr-1.5" /> Reset & force change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Provision from Fundle */}
      <ProvisionFromFundleDialog
        open={provisionOpen} onOpenChange={setProvisionOpen}
        clinics={clinics} onDone={load}
      />

      {/* Roles & Permissions info panel */}
      <Dialog open={rolesPanelOpen} onOpenChange={setRolesPanelOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <ShieldCheck className="w-4 h-4 text-indigo-700" /> Roles & Permissions
            </DialogTitle>
            <DialogDescription className="text-xs">
              HearClear OS role hierarchy — access scope grows with each level. Enforced at every API endpoint via <code>require_min_role</code>.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {ROLE_INFO.map((r, idx) => (
              <div key={r.key} className="flex items-start gap-3 p-2 rounded border border-stone-100 hover:bg-stone-50">
                <span className="text-[10px] font-mono text-stone-400 tabular-nums w-4">{ROLE_INFO.length - idx}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded font-bold">
                      {ROLE_LABELS[r.key]}
                    </span>
                    <span className="text-[10px] font-mono text-stone-400">{r.key}</span>
                  </div>
                  <div className="text-xs text-stone-600 mt-0.5">{r.scope}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-[11px] text-stone-500 flex items-start gap-1.5 border-t border-stone-200 pt-3">
            <Info className="w-3 h-3 mt-0.5 shrink-0" />
            <span>Higher roles inherit access from lower ones. To change role permissions in code, edit <code>backend/auth.py:ROLE_HIERARCHY</code> and re-run the API tests.</span>
          </div>
        </DialogContent>
      </Dialog>

      {waOpen && (
        <WhatsAppSendDialog
          open={!!waOpen} onOpenChange={(v) => !v && setWaOpen(null)}
          to={waOpen.phone} patientName={waOpen.name}
        />
      )}

      <HierarchyDialog open={hierarchyOpen} onOpenChange={setHierarchyOpen} />
    </div>
  );
}


function HierarchyDialog({ open, onOpenChange }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/users/hierarchy')
      .then(r => setTree(r.data))
      .catch(() => toast.error('Failed to load hierarchy'))
      .finally(() => setLoading(false));
  }, [open]);

  const renderNode = (n, depth = 0) => (
    <div key={n.id}>
      <div className="flex items-center gap-2 py-1 pr-2 rounded hover:bg-stone-50"
        style={{ paddingLeft: 8 + depth * 16 }}
        data-testid={`hierarchy-node-${n.id}`}>
        <span className="w-1 h-4 rounded-full"
          style={{ background: ['#2D3982', '#0284C7', '#0F766E', '#B45309', '#DC2626', '#7C3AED', '#4338CA'][depth % 7] }} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-stone-800 truncate">{n.name}</div>
          <div className="text-[10px] text-stone-500">
            {ROLE_LABELS[n.role] || n.role} · {n.email}
          </div>
        </div>
        {n.children?.length > 0 && (
          <span className="text-[9px] font-mono text-stone-400">
            {n.children.length} report{n.children.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
      {n.children?.map(c => renderNode(c, depth + 1))}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <GitBranch className="w-4 h-4 text-indigo-700" /> Team Hierarchy
          </DialogTitle>
          <DialogDescription className="text-xs">
            Org tree derived from each user&apos;s <code>reports_to</code>. Edit any user to change their manager.
            Ideal flow: CEO → Central Team → Business Head → Business Manager → Cluster Audio Head → Clinic Head → Clinic PC.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-96 overflow-auto border border-stone-200 rounded p-1" data-testid="hierarchy-tree">
          {loading && <div className="p-4 text-center text-stone-400 text-sm">Loading…</div>}
          {!loading && tree?.tree?.length === 0 && (
            <div className="p-4 text-center text-stone-400 text-sm">No users yet — provision some from Fundle.</div>
          )}
          {!loading && (tree?.tree || []).map(n => renderNode(n))}
        </div>
        <div className="text-[11px] text-stone-500">
          {tree ? `${tree.total} users · ${tree.tree?.length || 0} at top` : ''}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ProvisionFromFundleDialog({ open, onOpenChange, clinics, onDone }) {
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});   // { fundle_user_id: true }
  const [overrideRole, setOverrideRole] = useState('');
  const [overrideClinicIds, setOverrideClinicIds] = useState([]);
  const [query, setQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) return;
    setResult(null); setSelected({}); setQuery('');
    setLoading(true);
    api.get('/users/provisionable-from-fundle')
      .then(r => setPool(r.data.data || []))
      .catch(() => toast.error('Failed to load Fundle users'))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return pool.filter(u => {
      if (u.already_provisioned) return false;
      if (!q) return true;
      return (u.name || '').toLowerCase().includes(q)
        || (u.email || '').toLowerCase().includes(q)
        || (u.mobile || '').includes(q)
        || (u.fundle_role || '').toLowerCase().includes(q);
    });
  }, [pool, query]);

  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]).map(Number), [selected]);
  const allSelected = filtered.length > 0 && filtered.every(u => selected[u.fundle_user_id]);
  const toggleAll = () => {
    if (allSelected) {
      setSelected(s => {
        const next = { ...s };
        filtered.forEach(u => { delete next[u.fundle_user_id]; });
        return next;
      });
    } else {
      setSelected(s => {
        const next = { ...s };
        filtered.forEach(u => { next[u.fundle_user_id] = true; });
        return next;
      });
    }
  };

  const submit = async () => {
    if (selectedIds.length === 0) { toast.error('Pick at least one user'); return; }
    setSubmitting(true);
    try {
      const payload = {
        fundle_user_ids: selectedIds,
        default_password: 'HearClear@2026',
      };
      if (overrideRole) payload.override_role = overrideRole;
      if (overrideClinicIds.length) payload.override_clinic_ids = overrideClinicIds;
      const r = await api.post('/users/bulk-provision-from-fundle', payload);
      setResult(r.data);
      toast.success(`Provisioned ${r.data.created} · Skipped ${r.data.skipped}`);
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Provision failed'); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Zap className="w-4 h-4 text-emerald-700" /> Provision Logins from Fundle
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pick which Fundle users to onboard as HearClear OS logins. Each gets the temp password
            <code className="mx-1">HearClear@2026</code> and is forced to change it on first sign-in. Role is auto-mapped
            from the Fundle role unless you override it below.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3">
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm">
              <div className="font-semibold text-emerald-900">
                ✓ Provisioned {result.created} · Skipped {result.skipped} · Errors {result.errors?.length || 0}
              </div>
              <div className="text-xs mt-1">
                Temporary password: <code className="font-mono text-emerald-800">{result.temp_password}</code> (users must change on first login)
              </div>
            </div>
            <div className="max-h-64 overflow-auto border border-stone-200 rounded">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5 font-semibold">Name</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Email</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Mobile</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {(result.created_users || []).map(u => (
                    <tr key={u.id} className="border-t border-stone-100">
                      <td className="px-2 py-1">{u.name}</td>
                      <td className="px-2 py-1 font-mono">{u.email}</td>
                      <td className="px-2 py-1 font-mono">{u.mobile || '—'}</td>
                      <td className="px-2 py-1"><span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-stone-100 rounded font-semibold">{ROLE_LABELS[u.role] || u.role}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-stone-400 absolute left-2 top-2.5" />
                <Input value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search Fundle users…"
                  className="h-8 w-56 pl-7 text-xs" />
              </div>
              <Select value={overrideRole || 'auto'} onValueChange={(v) => setOverrideRole(v === 'auto' ? '' : v)}>
                <SelectTrigger className="h-8 w-52 text-xs" data-testid="provision-override-role">
                  <SelectValue placeholder="Role: auto from Fundle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Role: auto from Fundle</SelectItem>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>Force: {v}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => api.get('/users/provisionable-from-fundle')
                .then(r => setPool(r.data.data || []))} className="h-8">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Reload
              </Button>
              <div className="flex-1" />
              <span className="text-[10px] text-stone-500 tabular-nums">
                {selectedIds.length} selected · {filtered.length} available
              </span>
            </div>

            {/* Clinic multi-select for the whole batch */}
            <div>
              <Label className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold flex items-center gap-1.5">
                <Building2 className="w-3 h-3" /> Clinic access for this batch
                <span className="normal-case tracking-normal text-stone-400 font-normal">
                  (optional — leave empty to grant later)
                </span>
              </Label>
              <div className="mt-1 max-h-24 overflow-y-auto border border-stone-200 rounded p-1.5 grid grid-cols-3 gap-1">
                {clinics.map(c => {
                  const on = overrideClinicIds.includes(c.id);
                  return (
                    <button key={c.id} type="button"
                      onClick={() => setOverrideClinicIds(ids => on ? ids.filter(x => x !== c.id) : [...ids, c.id])}
                      className={`text-left text-xs px-1.5 py-1 rounded border transition-colors ${on ? 'bg-blue-50 border-blue-300 text-blue-900' : 'bg-white border-stone-200 hover:bg-stone-50'}`}>
                      <span className="mr-1">{on ? '✓' : '○'}</span> {c.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="max-h-80 overflow-auto border border-stone-200 rounded">
              <table className="w-full text-xs" data-testid="provision-table">
                <thead className="bg-stone-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-1.5 w-8">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="provision-select-all" />
                    </th>
                    <th className="text-left px-2 py-1.5 font-semibold">Name</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Email</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Mobile</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Fundle role</th>
                    <th className="text-left px-2 py-1.5 font-semibold">→ Local role</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => {
                    const on = !!selected[u.fundle_user_id];
                    return (
                      <tr key={u.fundle_user_id}
                        className={`border-t border-stone-100 cursor-pointer ${on ? 'bg-emerald-50' : 'hover:bg-stone-50'}`}
                        onClick={() => setSelected(s => ({ ...s, [u.fundle_user_id]: !on }))}
                        data-testid={`provision-row-${u.fundle_user_id}`}>
                        <td className="px-2 py-1"><input type="checkbox" checked={on} onChange={() => {}} /></td>
                        <td className="px-2 py-1 font-medium">{u.name}</td>
                        <td className="px-2 py-1 font-mono">{u.email}</td>
                        <td className="px-2 py-1 font-mono">{u.mobile || '—'}</td>
                        <td className="px-2 py-1">
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">{u.fundle_role || '—'}</span>
                        </td>
                        <td className="px-2 py-1">
                          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-indigo-100 text-indigo-800 rounded font-bold">
                            {overrideRole ? ROLE_LABELS[overrideRole] : (ROLE_LABELS[u.mapped_role] || u.mapped_role)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={6} className="p-4 text-center text-stone-400">
                      All eligible Fundle users are already provisioned.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <Button onClick={() => onOpenChange(false)}>Done</Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting || selectedIds.length === 0}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="provision-submit">
                {submitting ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1.5" />}
                Provision {selectedIds.length} user{selectedIds.length === 1 ? '' : 's'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
